import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { TablePaginationConfig } from 'antd';
import {
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
} from 'antd/es/table/interface';

interface UseTableOptions<T, P = any> {
  queryKey: string | unknown[];
  queryFn: (
    page: number,
    pageSize: number,
    params?: P
  ) => Promise<{
    total: number;
    currentPage: number;
    pageSize: number;
    records: T[];
  }>;
  defaultPageSize?: number;
  defaultParams?: P;
  /** 挂载时是否重新请求（默认 false，投稿页等需要每次进入都请求时可设为 true） */
  refetchOnMount?: boolean;
  onTableChange?: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<T> | SorterResult<T>[],
    extra: TableCurrentDataSource<T>
  ) => void;
}

export function useTable<T, P = any>({
  queryKey,
  queryFn,
  defaultPageSize = 20,
  defaultParams,
  refetchOnMount = false,
  onTableChange,
}: UseTableOptions<T, P>) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [params, setParams] = useState<P | undefined>(defaultParams);

  const paramsKey = JSON.stringify(params ?? {});
  const { data, isLoading, refetch } = useQuery({
    queryKey: [queryKey, currentPage, pageSize, paramsKey],
    queryFn: () => queryFn(currentPage, pageSize, params),
    keepPreviousData: true,
    staleTime: 30 * 1000,
    refetchOnMount,
    refetchOnWindowFocus: false,
  });

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<T> | SorterResult<T>[],
    extra: TableCurrentDataSource<T>
  ) => {
    if (pagination.current) {
      setCurrentPage(pagination.current);
    }
    if (pagination.pageSize) {
      setPageSize(pagination.pageSize);
    }
    onTableChange?.(pagination, filters, sorter, extra);
  };

  const refresh = () => {
    setCurrentPage(1);
    refetch();
  };

  const updateParams = (newParams: P, options?: { resetPage?: boolean }) => {
    setParams(newParams);
    if (options?.resetPage !== false) {
      setCurrentPage(1);
    }
  };

  const reload = () => {
    refetch();
  }

  return {
    data: data?.records || [],
    total: data?.total || 0,
    currentPage,
    pageSize,
    isLoading,
    refresh,
    updateParams,
    handleTableChange,
    reload,
    setCurrentPage,
    setPageSize,
  };
}
